import type { Seat } from '@campfire/engine';
import { BISHOP, KING, KNIGHT, PAWN, QUEEN, ROOK, SEAT_NAMES, SIZE, ownerOf, typeOf, type ChessView, type Promo } from '@campfire/game-chess';
import { label } from '@campfire/grid';
import { h } from '../dom.ts';
import { name, type GameUi, type TableCtx } from '../games.ts';
import { gridBoard } from './grid-board.ts';

/** Solid glyphs for both sides (colour comes from CSS); the text selector keeps iOS from drawing the pawn as an emoji. */
const GLYPH: Record<number, string> = { [PAWN]: '♟︎', [KNIGHT]: '♞', [BISHOP]: '♝', [ROOK]: '♜', [QUEEN]: '♛', [KING]: '♚' };
const WORTH: Record<number, number> = { [PAWN]: 1, [KNIGHT]: 3, [BISHOP]: 3, [ROOK]: 5, [QUEEN]: 9, [KING]: 0 };
const PIECE_NAMES: Record<number, string> = { [PAWN]: 'pawn', [KNIGHT]: 'knight', [BISHOP]: 'bishop', [ROOK]: 'rook', [QUEEN]: 'queen', [KING]: 'king' };
const PROMO_TYPES: readonly (readonly [Promo, number])[] = [['Q', QUEEN], ['R', ROOK], ['B', BISHOP], ['N', KNIGHT]];

const glyph = (seat: Seat, type: number, extra = '') => h(`span.ch-piece.s${seat}${extra}`, { 'aria-hidden': 'true' }, GLYPH[type] as string);

function player(ctx: TableCtx, v: ChessView, s: Seat, pos: string) {
  const mine = v.captured[s] as readonly number[];
  const theirs = v.captured[1 - s] as readonly number[];
  const lost = mine.slice().sort((a, b) => (WORTH[b] ?? 0) - (WORTH[a] ?? 0));
  const lead = mine.reduce((n, t) => n + (WORTH[t] ?? 0), 0) - theirs.reduce((n, t) => n + (WORTH[t] ?? 0), 0);
  return h(
    `div.ch-player${v.turn === s ? '.turn' : ''}`,
    { 'data-testid': `ch-player-${pos}`, 'data-seat': s },
    h(`span.ch-swatch.s${s}`),
    h('span.ch-name', {}, name(ctx, s)),
    h('span.muted', {}, SEAT_NAMES[s]),
    h('span.ch-captured', { 'data-testid': `ch-captured-${s}`, 'data-count': lost.length }, lost.map((t) => glyph(1 - s as Seat, t, '.small')), lead > 0 ? h('span.ch-lead', {}, `+${lead}`) : null),
  );
}

function statusText(ctx: TableCtx, v: ChessView): string {
  if (v.result) {
    const w = v.result.winners[0];
    return v.result.draw ? `Draw: ${v.result.reason ?? ''}` : `${w === ctx.you ? 'You win' : `${name(ctx, w as Seat)} wins`}: ${v.result.reason ?? ''}`;
  }
  const check = v.check !== null ? ' Check!' : '';
  if (v.legal.length > 0) return `Your move.${check}`;
  return `${name(ctx, v.turn as Seat)} ${v.turn === ctx.you ? 'are' : 'is'} to move.${check}`;
}

/** Numbered move list, e.g. "1. e4 e5 2. Nf3". */
function moveList(v: ChessView): HTMLElement {
  const kids: (HTMLElement | string)[] = [];
  v.sans.forEach((san, i) => {
    const ply = i + v.startTurn;
    if (ply % 2 === 0) kids.push(h('span.ch-num', {}, `${ply / 2 + 1}.`));
    else if (i === 0) kids.push(h('span.ch-num', {}, `${(ply - 1) / 2 + 1}...`));
    kids.push(h('span.ch-san', { 'data-testid': `ch-san-${i}` }, san));
  });
  const el = h('div.ch-moves', { 'data-testid': 'ch-moves', 'data-count': v.sans.length }, kids.length > 0 ? kids : h('span.muted', {}, 'No moves yet.'));
  requestAnimationFrame(() => {
    el.scrollTop = el.scrollHeight;
  });
  return el;
}

export const chessUi: GameUi = {
  id: 'chess',
  privateHands: false,
  seatInfo: (ctx, s) => {
    const v = ctx.msg.view as ChessView;
    return v.turn === s && v.check !== null ? 'in check' : '';
  },
  render(ctx) {
    const v = ctx.msg.view as ChessView;
    const me = ctx.mySeat;
    const over = v.result !== null;
    const sig = `${v.moves}-${over}`;
    if (ctx.local.get<string>('ch-sig') !== sig) {
      ctx.local.set('ch-sig', sig);
      ctx.local.set('ch-sel', null);
      ctx.local.set('ch-promo', null);
      ctx.local.set('ch-resign', false);
    }
    const sel = ctx.local.get<number | null>('ch-sel') ?? null;
    const promo = ctx.local.get<{ from: number; to: number } | null>('ch-promo') ?? null;
    const mine = v.legal.length > 0;
    const origins = new Set(v.legal.map((m) => m.from));
    const targets = new Set(v.legal.filter((m) => m.from === sel).map((m) => m.to));

    const tap = (i: number) => {
      if (promo) return;
      if (sel !== null && targets.has(i)) {
        if (v.legal.some((m) => m.from === sel && m.to === i && m.promo)) return ctx.local.set('ch-promo', { from: sel, to: i });
        ctx.local.set('ch-sel', null);
        return ctx.act({ type: 'move', from: sel, to: i });
      }
      if (origins.has(i)) ctx.local.set('ch-sel', sel === i ? null : i);
    };

    const board = gridBoard({
      size: SIZE,
      flip: me === 1,
      testid: 'ch-board',
      coords: true,
      cell: ({ index: i, x, y }) => {
        const p = v.board[i] as number;
        const owner = ownerOf(p);
        const pawnMover = sel !== null && typeOf(v.board[sel] as number) === PAWN;
        const isTarget = targets.has(i);
        const capture = isTarget && (p !== 0 || (pawnMover && i % 8 !== (sel as number) % 8));
        const tappable = mine && !promo && (origins.has(i) || isTarget);
        const cls = [
          'ch-sq',
          (x + y) % 2 === 0 ? 'dark' : 'light',
          sel === i ? 'sel' : '',
          isTarget ? (capture ? 'cap' : 'dest') : '',
          mine && sel === null && origins.has(i) ? 'movable' : '',
          i === v.last?.from ? 'from' : '',
          i === v.last?.to ? 'to' : '',
          i === v.check ? 'check' : '',
        ].filter(Boolean).join(' ');
        const tag = label(SIZE, i);
        const type = typeOf(p);
        return {
          classes: cls,
          label: owner === null ? tag : `${tag}, ${SEAT_NAMES[owner]} ${PIECE_NAMES[type]}`,
          attrs: { 'data-testid': `ch-sq-${tag}`, 'data-sq': tag },
          ...(tappable ? { onTap: () => tap(i) } : {}),
          content: owner === null ? null : h(`span.ch-piece.s${owner}`, { 'data-testid': `ch-piece-${tag}`, 'data-owner': owner, 'data-type': type }, GLYPH[type] as string),
        };
      },
    });

    const opp = me === null ? 1 : 1 - me;
    const self = me === null ? 0 : me;

    let controls: HTMLElement | null = null;
    if (me !== null && !over) {
      const armed = ctx.local.get<boolean>('ch-resign') === true;
      const offered = v.drawOffer !== null && v.drawOffer !== me;
      controls = h(
        'div.row.actions.ch-actions',
        { 'data-testid': 'ch-actions' },
        offered
          ? [
              h('button.primary', { 'data-testid': 'ch-accept', onclick: () => ctx.act({ type: 'acceptDraw' }) }, 'Accept draw'),
              h('button', { 'data-testid': 'ch-decline', onclick: () => ctx.act({ type: 'declineDraw' }) }, 'Decline'),
            ]
          : h('button', { 'data-testid': 'ch-offer', disabled: v.drawOffer === me, onclick: () => ctx.act({ type: 'offerDraw' }) }, v.drawOffer === me ? 'Draw offered' : 'Offer draw'),
        armed
          ? [
              h('button.danger', { 'data-testid': 'ch-resign-confirm', onclick: () => ctx.act({ type: 'resign' }) }, 'Confirm resign'),
              h('button', { 'data-testid': 'ch-resign-cancel', onclick: () => ctx.local.set('ch-resign', false) }, 'Cancel'),
            ]
          : h('button', { 'data-testid': 'ch-resign', onclick: () => ctx.local.set('ch-resign', true) }, 'Resign'),
      );
    }

    const picker = promo
      ? h(
          'div.row.ch-promo',
          { 'data-testid': 'ch-promo' },
          h('span.muted', {}, 'Promote to'),
          PROMO_TYPES.map(([letter, type]) =>
            h(
              'button.ch-promo-btn',
              {
                'data-testid': `ch-promo-${letter}`,
                'aria-label': PIECE_NAMES[type] as string,
                onclick: () => {
                  ctx.local.set('ch-promo', null);
                  ctx.local.set('ch-sel', null);
                  ctx.act({ type: 'move', from: promo.from, to: promo.to, promo: letter });
                },
              },
              glyph(me as Seat, type),
            ),
          ),
          h('button', { 'data-testid': 'ch-promo-cancel', onclick: () => ctx.local.set('ch-promo', null) }, 'Cancel'),
        )
      : null;

    return h(
      'div.ch-table',
      {},
      player(ctx, v, opp, 'top'),
      board,
      player(ctx, v, self, 'bottom'),
      h('div.event', { 'data-testid': 'ch-status' }, statusText(ctx, v)),
      picker,
      v.drawOffer !== null && !over ? h('div.muted', { 'data-testid': 'ch-offer-note' }, `${name(ctx, v.drawOffer)} offered a draw.`) : null,
      controls,
      moveList(v),
    );
  },
};
